import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, MessageCircle, Users, FileText, MessagesSquare, Loader2, X, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const MIN_Q = 2;
const MAX_Q = 64;
const DEBOUNCE_MS = 350;
const RECENT_LS_KEY = "doccircle.recentSearches.v1";
const RECENT_MAX = 5;

const GROUP_META = {
  dm_message:      { label: "Direct Messages",  icon: MessageCircle },
  group_message:   { label: "Circle Messages",  icon: MessagesSquare },
  circle_post:     { label: "Circle Posts",     icon: FileText },
  circle_comment:  { label: "Circle Comments",  icon: Users },
};
const ORDER = ["dm_message", "group_message", "circle_post", "circle_comment"];
const ALL_TYPES = new Set(ORDER);

function Highlight({ text, term }) {
  if (!term || !text) return <>{text}</>;
  const t = term.toLowerCase();
  const parts = [];
  let i = 0;
  const lower = text.toLowerCase();
  while (i < text.length) {
    const j = lower.indexOf(t, i);
    if (j < 0) { parts.push(text.slice(i)); break; }
    if (j > i) parts.push(text.slice(i, j));
    parts.push(<mark key={j} className="bg-yellow-200 text-inherit rounded px-0.5">{text.slice(j, j + term.length)}</mark>);
    i = j + term.length;
  }
  return <>{parts.map((p, idx) => <React.Fragment key={idx}>{p}</React.Fragment>)}</>;
}

function deepLinkFor(r) {
  switch (r.result_type) {
    case "dm_message":     return r.conversation_id ? `/messages?conversation=${encodeURIComponent(r.conversation_id)}` : "/messages";
    case "group_message":  return r.circle_id ? `/messages?circle=${encodeURIComponent(r.circle_id)}` : "/messages";
    case "circle_post":    return r.circle_id ? `/circles/${encodeURIComponent(r.circle_id)}?post=${encodeURIComponent(r.id)}` : "/circles";
    case "circle_comment": return r.circle_id ? `/circles/${encodeURIComponent(r.circle_id)}?post=${encodeURIComponent(r.parent_id || "")}&comment=${encodeURIComponent(r.id)}` : "/circles";
    default: return "/";
  }
}

function readRecent() {
  try {
    const raw = localStorage.getItem(RECENT_LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((q) => typeof q === "string").slice(0, RECENT_MAX) : [];
  } catch (_) { return []; }
}

function writeRecent(term) {
  try {
    const t = (term || "").trim();
    if (t.length < MIN_Q) return;
    const prev = readRecent().filter((q) => q.toLowerCase() !== t.toLowerCase());
    const next = [t, ...prev].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_LS_KEY, JSON.stringify(next));
  } catch (_) { /* private mode / storage full — silent */ }
}

export default function Search() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const initial = sp.get("q") || "";
  const initialCircle = sp.get("circle") || "";
  const initialTypes = sp.get("types") || "";
  const [q, setQ] = useState(initial);
  const [types, setTypes] = useState(() => {
    const t = initialTypes.split(",").filter(Boolean).filter((x) => ALL_TYPES.has(x));
    return new Set(t.length ? t : ORDER);
  });
  const [circleId, setCircleId] = useState(initialCircle);
  const [circleName, setCircleName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [ranQuery, setRanQuery] = useState("");
  const [recent, setRecent] = useState(readRecent());
  const debounceRef = useRef(null);

  // Resolve circle name for the scope chip when narrowing to a Circle.
  useEffect(() => {
    let cancelled = false;
    if (!circleId) { setCircleName(""); return; }
    (async () => {
      try {
        const r = await api.get(`/circles/${circleId}`);
        if (!cancelled) setCircleName(r.data?.name || circleId);
      } catch (_) { if (!cancelled) setCircleName(circleId); }
    })();
    return () => { cancelled = true; };
  }, [circleId]);

  const runSearch = useCallback(async (term, cursor = null, append = false) => {
    const t = (term || "").trim();
    if (t.length < MIN_Q) {
      setResults([]); setNextCursor(null); setError(""); setRanQuery("");
      return;
    }
    if (t.length > MAX_Q) { setError(`Query too long (max ${MAX_Q} chars).`); return; }
    setLoading(true);
    setError("");
    try {
      const params = { q: t, limit: 20 };
      if (cursor) params.cursor = cursor;
      const typeList = Array.from(types);
      if (typeList.length > 0 && typeList.length < ORDER.length) {
        params.types = typeList.join(",");
      }
      if (circleId) params.circle_id = circleId;
      const r = await api.get("/search/messages", { params });
      const body = r.data || {};
      setResults((prev) => append ? [...prev, ...(body.results || [])] : (body.results || []));
      setNextCursor(body.next_cursor || null);
      setRanQuery(t);
      if (!append) {
        writeRecent(t);
        setRecent(readRecent());
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Search failed. Please try again.");
      if (!append) { setResults([]); setNextCursor(null); }
    } finally {
      setLoading(false);
    }
  }, [types, circleId]);

  // Debounced search-as-you-type + URL sync
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = {};
      if (q) next.q = q;
      if (circleId) next.circle = circleId;
      const typeList = Array.from(types);
      if (typeList.length > 0 && typeList.length < ORDER.length) next.types = typeList.join(",");
      setSp(next, { replace: true });
      runSearch(q, null, false);
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, types, circleId]);

  const toggleType = (t) => {
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      if (next.size === 0) ORDER.forEach((k) => next.add(k));
      return next;
    });
  };
  const resetTypes = () => setTypes(new Set(ORDER));
  const clearCircleFilter = () => setCircleId("");
  const clearRecent = () => {
    try { localStorage.removeItem(RECENT_LS_KEY); } catch (_) {}
    setRecent([]);
  };

  const grouped = useMemo(() => {
    const g = { dm_message: [], group_message: [], circle_post: [], circle_comment: [] };
    for (const r of results) if (g[r.result_type]) g[r.result_type].push(r);
    return g;
  }, [results]);

  const totalCount = results.length;
  const trimmedQ = q.trim();
  const tooShort = trimmedQ.length > 0 && trimmedQ.length < MIN_Q;
  const allTypesActive = types.size === ORDER.length;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="search-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-serif font-black tracking-tight">Search</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Find messages, posts, and comments across the Circles and conversations you already have access to.
        </p>
      </div>

      <div className="relative">
        <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value.slice(0, MAX_Q))}
          placeholder="Search your messages, posts, and comments… (press Esc to clear)"
          autoFocus
          className="pl-9"
          maxLength={MAX_Q}
          aria-label="Search query"
          onKeyDown={(e) => { if (e.key === "Escape") setQ(""); }}
        />
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2" data-testid="search-filters">
        {ORDER.map((t) => {
          const active = types.has(t);
          const { label } = GROUP_META[t];
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggleType(t)}
              data-testid={`search-filter-${t}`}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                active ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground border-border hover:bg-secondary"
              }`}
              aria-pressed={active}
            >
              {label}
            </button>
          );
        })}
        {!allTypesActive && (
          <button
            type="button"
            onClick={resetTypes}
            data-testid="search-filter-reset"
            className="text-xs px-2 py-1 rounded-full text-muted-foreground hover:text-foreground"
          >
            All sources
          </button>
        )}
        {circleId && (
          <span
            data-testid="search-scope-chip"
            className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-secondary text-secondary-foreground"
            title="Filtering to a specific Circle"
          >
            Circle: {circleName || circleId}
            <button
              type="button"
              onClick={clearCircleFilter}
              data-testid="search-scope-clear"
              className="ml-1 hover:text-foreground"
              aria-label="Clear Circle filter"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
      </div>

      {/* Recent searches */}
      {!ranQuery && recent.length > 0 && (
        <div data-testid="search-recent">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Clock className="h-3 w-3" /> Recent searches
            </div>
            <button
              type="button"
              onClick={clearRecent}
              data-testid="search-recent-clear"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {recent.map((r, i) => (
              <button
                key={r + i}
                type="button"
                onClick={() => setQ(r)}
                data-testid={`search-recent-item-${i}`}
                className="text-xs px-3 py-1 rounded-full border border-border hover:bg-secondary"
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {tooShort && (
        <p className="text-xs text-muted-foreground" data-testid="search-too-short">
          Type at least {MIN_Q} characters to search.
        </p>
      )}

      {error && (
        <div className="text-sm text-red-600" data-testid="search-error">{error}</div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="search-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Searching…
        </div>
      )}

      {!loading && !error && ranQuery && totalCount === 0 && (
        <Card data-testid="search-empty">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              No results for <span className="font-medium">"{ranQuery}"</span> in the content you can access.
            </p>
          </CardContent>
        </Card>
      )}

      {totalCount > 0 && (
        <div className="space-y-8" data-testid="search-results">
          {ORDER.map((key) => {
            const rows = grouped[key];
            if (!rows || rows.length === 0) return null;
            const { label, icon: Icon } = GROUP_META[key];
            return (
              <section key={key} data-testid={`search-group-${key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="h-4 w-4 text-primary" />
                  <h2 className="text-base font-semibold">{label}</h2>
                  <span className="text-xs text-muted-foreground">({rows.length})</span>
                </div>
                <div className="space-y-2">
                  {rows.map((r) => (
                    <button
                      key={`${r.result_type}:${r.id}`}
                      data-testid={`search-result-${r.result_type}-${r.id}`}
                      onClick={() => nav(deepLinkFor(r))}
                      className="w-full text-left rounded-lg border border-border bg-card hover:bg-secondary/60 transition-colors p-4"
                    >
                      <div className="text-sm leading-snug">
                        <Highlight text={r.snippet || ""} term={ranQuery} />
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <span>{new Date(r.created_at).toLocaleString()}</span>
                        <span>·</span>
                        <span>Open in context →</span>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}

          {nextCursor && (
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={loading}
                data-testid="search-load-more"
                onClick={() => runSearch(ranQuery, nextCursor, true)}
              >
                {loading ? "Loading…" : "Load more results"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
